import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  InputBase,
  alpha,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import FolderIcon from "@mui/icons-material/Folder";
import DocumentIcon from "@mui/icons-material/PictureAsPdf";
import GraphicEqIcon from "@mui/icons-material/GraphicEq";
import ChevronRight from "@mui/icons-material/ChevronRight";
import SearchIcon from "@mui/icons-material/Search";
import { useTranslation } from "react-i18next";
import clsx from "clsx";
import _ from "lodash";

const MediaDialog = (props, ref) => {
  const [windowWidth, setWindowWidth] = React.useState(window.innerWidth);
  React.useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);
  const classes = useStyles({ windowWidth });

  const { media, userId, onSubmit } = props;
  const [open, setOpen] = React.useState(false);
  const [mediaId, setMediaId] = React.useState([]);
  const [path, setPath] = React.useState([]);
  const [visibleFolders, setVisibleFolders] = React.useState([]);
  const [visibleFiles, setVisibleFiles] = React.useState([]);

  const getMediaInPath = () => {
    if (!path || path.length === 0) {
      setVisibleFolders(
        media.filter((m) => m.mediaType === "folder" && !m.path)
      );
      setVisibleFiles(media.filter((m) => m.mediaType !== "folder" && !m.path));
    } else {
      setVisibleFolders(
        media.filter(
          (m) => m.mediaType === "folder" && m.path === path.join("/")
        )
      );
      setVisibleFiles(
        media.filter(
          (m) => m.mediaType !== "folder" && m.path === path.join("/")
        )
      );
    }
  };
  React.useEffect(() => {
    getMediaInPath();
  }, [path]);
  const handleSetPath = (folderId) => {
    if (folderId === "home") {
      setPath([]);
      return;
    }
    const folder = media.find((m) => m.id === folderId);
    setPath([...(folder.path ? folder.path.split("/") : []), folder.id]);
  };

  const { t } = useTranslation();

  React.useImperativeHandle(ref, () => ({
    open: () => {
      setOpen(true);
      setMediaId();
      setPath([]);
    },
  }));

  const handleClose = () => {
    setOpen(false);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setOpen(false);
    let mediaArray = [];
    for (const id of mediaId) {
      mediaArray = [...(mediaArray || []), media.find((m) => m.id === id)];
    }
    onSubmit(mediaArray);
  };

  const [searchValue, setSearchValue] = React.useState("");
  const applySearch = (item) => {
    const searchVal = searchValue.trim().toLowerCase();
    if (!searchVal) return true;
    if (item.mediaType === "folder") return false;
    const terms = searchVal.split(" ");
    let result = true;
    for (const term of terms) {
      const searchTerm = new RegExp("^(.*)" + term + "(.*)$");
      result = result && searchTerm.test(item.title.toLowerCase());
    }
    return result;
  };

  const renderFolder = (folder) => {
    return (
      <div
        className={classes.folder}
        key={folder.id}
        onClick={() => handleSetPath(folder.id)}
      >
        <FolderIcon className={classes.folderIcon} />
        <div className={classes.folderName}>{folder.title}</div>
      </div>
    );
  };

  const handleSelect = (id) => {
    if (!id) return;
    if (mediaId && mediaId.includes(id)) {
      setMediaId(mediaId.filter((i) => i !== id));
      return;
    }
    setMediaId([...(mediaId || []), id]);
  };

  const renderFile = (media) => {
    return (
      <div
        className={clsx(classes.media, {
          [classes.selected]: mediaId && mediaId.includes(media.id),
        })}
        key={media.id}
        onClick={() => handleSelect(media.id)}
      >
        {media.mediaType === "image" && (
          <div
            className={classes.thumbnail}
            style={{
              backgroundImage: `url(${process.env.REACT_APP_STATIC_URL}/${userId}/${media.id})`,
            }}
          />
        )}
        {media.mediaType === "video" && (
          <div
            className={classes.thumbnail}
            style={{
              backgroundImage: `url(${process.env.REACT_APP_STATIC_URL}/${userId}/${media.id}.0000001.jpg)`,
            }}
          />
        )}
        {media.mediaType === "application" && (
          <div
            className={classes.thumbnail}
            style={{
              backgroundImage: `url(${process.env.REACT_APP_STATIC_URL}/${userId}/${media.id}.jpg)`,
            }}
          />
        )}
        {media.mediaType === "audio" && (
          <GraphicEqIcon className={classes.folderIcon} />
        )}
        {media.mediaType === "application" && (
          <DocumentIcon className={classes.folderIcon} />
        )}
        <div className={classes.folderName}>{media.title}</div>
      </div>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      classes={{ paper: classes.dialog }}
    >
      <form onSubmit={handleSubmit}>
        <DialogTitle>{t("party.sendMediaTitle")}:</DialogTitle>
        <DialogContent>
          <div className={classes.search}>
            <div className={classes.searchIcon}>
              <SearchIcon />
            </div>
            <InputBase
              classes={{
                root: classes.searchField,
                input: classes.searchInput,
              }}
              inputProps={{ "aria-label": "search" }}
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
            />
          </div>
          {!searchValue.trim() && (
            <div className={classes.breadcrumbs}>
              <Button
                size="small"
                classes={{
                  root: classes.pathButton,
                  label: classes.pathButtonLabel,
                }}
                onClick={() => handleSetPath("home")}
              >
                {t("media.homeTitle")}
              </Button>
              {path.length > 0 && (
                <ChevronRight
                  fontSize="small"
                  className={classes.pathSeparator}
                />
              )}
              {path.length > 2 && (
                <>
                  <Button
                    size="small"
                    classes={{
                      root: classes.pathButton,
                      label: classes.pathButtonLabel,
                    }}
                    disabled
                  >
                    ...
                  </Button>
                  <ChevronRight
                    fontSize="small"
                    className={classes.pathSeparator}
                  />
                </>
              )}
              {path.slice(-2).map((p, index) => (
                <React.Fragment key={p}>
                  <Button
                    size="small"
                    classes={{
                      root: classes.pathButton,
                      label: classes.pathButtonLabel,
                    }}
                    onClick={() => handleSetPath(p)}
                  >
                    {media.find((m) => m.id === p).title}
                  </Button>
                  {index < path.slice(-2).length - 1 && (
                    <ChevronRight
                      fontSize="small"
                      className={classes.pathSeparator}
                    />
                  )}
                </React.Fragment>
              ))}
            </div>
          )}
          <div className={classes.mediaArea}>
            {!searchValue.trim() &&
              _.sortBy(visibleFolders, ["title"]).map(renderFolder)}
            {!searchValue.trim() &&
              _.sortBy(visibleFiles, ["title"]).map(renderFile)}
            {!!searchValue.trim() &&
              _.sortBy(media.filter(applySearch), ["title"]).map(renderFile)}
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="inherit">
            {t("general.cancel")}
          </Button>
          <Button
            type="submit"
            color="primary"
            variant="contained"
            disabled={!mediaId}
          >
            {t("general.send")}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

const useStyles = makeStyles((theme) => ({
  dialog: {
    margin: theme.spacing(1),
    maxHeight: `calc(100% - ${theme.spacing(2)})`,
  },
  search: {
    position: "relative",
    borderRadius: theme.shape.borderRadius,
    backgroundColor: alpha("rgb(180, 180, 180)", 0.15),
    "&:hover": {
      backgroundColor: alpha("rgb(180, 180, 180)", 0.25),
    },
    width: "auto",
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 10,
  },
  searchIcon: {
    height: "100%",
    width: theme.spacing(5),
    pointerEvents: "none",
    display: "flex",
    justifyContent: "center",
  },
  searchField: {
    color: "inherit",
  },
  searchInput: {
    padding: theme.spacing(1, 1, 1, 0),
    width: "100%",
  },
  breadcrumbs: {
    paddingBottom: 10,
    borderBottomWidth: 1,
    borderColor: "rgba(160, 160, 160, 0.6)",
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
  },
  pathButton: {
    textTransform: "none",
    fontWeight: "normal",
    fontSize: "0.9rem",
    color: "#333",
    paddingBottom: 3,
    paddingLeft: 8,
    paddingRight: 8,
    minWidth: 30,
    "& span": {
      pointerEvents: "none",
    },
    justifyContent: "flex-start",
  },
  pathButtonLabel: {
    overflow: "hidden",
    whiteSpace: "nowrap",
  },
  pathSeparator: {
    color: "#999",
    marginLeft: -5,
    marginRight: -5,
  },
  mediaArea: {
    maxHeight: "calc(100vh - 240px)",
    overflowY: "auto",
    width: 400,
    maxWidth: (props) => props.windowWidth - theme.spacing(4),
    marginLeft: -theme.spacing(2),
    [theme.breakpoints.up("sm")]: {
      marginLeft: 0,
    },
  },
  folder: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    height: 40,
    width: 400,
    maxWidth: (props) => props.windowWidth - theme.spacing(4),
    backgroundColor: "#f8f8f8",
    borderRadius: 5,
    marginBottom: 5,
    paddingLeft: 10,
    paddingRight: 10,
    cursor: "pointer",
    "&:hover": {
      backgroundColor: "#f2f2f2",
    },
  },
  media: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    height: 80,
    width: 400,
    maxWidth: (props) => props.windowWidth - theme.spacing(4),
    backgroundColor: "#f8f8f8",
    borderRadius: 5,
    marginBottom: 5,
    paddingLeft: 10,
    paddingRight: 10,
    cursor: "pointer",
    "&:hover": {
      backgroundColor: "#f2f2f2",
    },
  },
  selected: {
    backgroundColor: "#ddd",
    "&:hover": {
      backgroundColor: "#ddd",
    },
  },
  folderIcon: {
    color: "#666",
  },
  thumbnail: {
    width: 60,
    height: 60,
    backgroundSize: "cover",
    borderRadius: 3,
    marginRight: 5,
  },
  folderName: {
    flex: 1,
    fontSize: 17,
    textJustify: "baseline",
    paddingLeft: 5,
    marginBottom: -3,
    color: "#444",
    whiteSpace: "pre",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
}));

export default React.forwardRef(MediaDialog);
